import type { LiteralUnion } from "type-fest";

import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { SaleProgress, SalesProvider, useIntegratedFlowStore, useProductOfferStore } from "@expert/sales-tools";

import { getRootDispatcher } from "@/analytics";
import { type HoneycombEnrolledProducts, sendToHoneycomb } from "@/analytics/sendToHoneycomb";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

type EnrollmentStatus = LiteralUnion<"SUCCESS" | "FAILURE" | "DECLINED", string>;

export function updatePrimaryProductSaleProgress({
    status,
    offerId,
    salesProvider,
}: {
    status: EnrollmentStatus;
    offerId: string;
    salesProvider: SalesProvider;
}): void {
    const { getPrimaryProducts, updateProduct, isPrimaryProductInProgress, getProducts, getProductInfo } =
        useProductOfferStore.getState();
    const { updateIntegratedStep } = useIntegratedFlowStore.getState();
    const sessionId = useProductOfferStore.getState().getSessionIdForProductId(offerId);
    const primaryProducts = sessionId ? getPrimaryProducts(sessionId) : [];
    const offersList = primaryProducts.map((p) => p.id);

    const provider = getProvider(salesProvider);

    if (!sessionId || primaryProducts.length <= 0 || !offersList.includes(offerId)) {
        return void getRootDispatcher().dispatchBusinessEvent(`Previous${provider}EnrollmentEventReceived`, {
            productOfferId: offerId,
            enrollmentStatus: status,
        });
    }

    const progress = convertToSaleProgress(status);

    if (progress == null) {
        return void getRootDispatcher().dispatchBusinessEvent(`Unknown${provider}EnrollmentEventReceived`, {
            productOfferId: offerId,
            enrollmentStatus: status,
        });
    }

    startDynatraceAction("SaleEnrollment", offerId);
    updateProduct(sessionId, offerId, { saleProgress: progress });

    getLogger({ module: "updatePrimaryProductSaleProgress" }).info(
        { enrollmentStatus: status, productOfferId: offerId, sessionId, salesProvider },
        "Enrollment outcome",
    );

    void getRootDispatcher().dispatchBusinessEvent(`${provider}EnrollmentEventReceived`, {
        productOfferId: offerId,
        enrollmentStatus: status,
        sessionId,
    });

    const products = getProducts(sessionId);
    const enrolledProducts: HoneycombEnrolledProducts[] = products
        .filter((p) => p.saleProgress === SaleProgress.Success)
        .map((p) => {
            return {
                productSku: p.productSku,
                productOfferId: p.id,
                enrollmentStatus: p.saleProgress,
                statusOrigin: p.salesProvider,
                displayName: getProductInfo(sessionId, p.productSku)?.displayName,
            };
        });

    const enrolledProductSku = products.filter((p) => p.id === offerId).at(0)?.productSku;
    const enrollmentStatus = convertToSaleProgress(status) ?? SaleProgress.Failure;
    const newEnrollment: HoneycombEnrolledProducts = {
        enrollmentStatus,
        productSku: enrolledProductSku ?? "",
        productOfferId: offerId,
        statusOrigin: salesProvider,
        displayName: getProductInfo(sessionId, enrolledProductSku)?.displayName,
    };
    const honeycombEnrollment: HoneycombEnrolledProducts[] = enrolledProducts;

    if (!honeycombEnrollment.some((p) => p.productSku === newEnrollment.productSku)) {
        honeycombEnrollment.concat([newEnrollment]);
    }

    sendToHoneycomb(sessionId, { enrollment: honeycombEnrollment });

    const isSuccess = enrollmentStatus === SaleProgress.Success;
    const eventName = isSuccess ? "EnrollmentSuccess" : "EnrollmentFailure";

    trackFullStoryEvent(eventName, {
        productSku: enrolledProductSku ?? "",
        productOfferId: offerId,
        sessionId,
        enrollmentStatus: enrollmentStatus,
        statusOrigin: salesProvider,
    });

    endDynatraceAction("SaleEnrollment", isSuccess ? undefined : { taskId: offerId, error: eventName });

    if (isPrimaryProductInProgress(sessionId)) {
        updateIntegratedStep(sessionId, "SuggestedOfferStep");
    }
}

function convertToSaleProgress(status?: EnrollmentStatus): SaleProgress | undefined {
    switch (status) {
        case "SUCCESS":
        case "success":
            return SaleProgress.Success;
        case "failure":
        case "FAILURE":
            return SaleProgress.Failure;
        case "DECLINED":
            return SaleProgress.Declined;
    }
}

export function getProvider(provider: SalesProvider) {
    switch (provider) {
        case SalesProvider.ASP:
            return "ASP";
        case SalesProvider.EXPERT_SALES:
            return "EXPERTSALES";
        default:
            return undefined;
    }
}
